import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Flame, TrendingUp, ArrowRight, Calendar } from 'lucide-react';
import { ContentIdea } from '@/types/dashboard';
import { useLanguage } from '@/contexts/LanguageContext';

interface TopIdeasBoxProps {
  ideas: ContentIdea[];
  onIdeaClick: (idea: ContentIdea) => void;
  onSeeMore: () => void;
  limit?: number;
}

const TopIdeasBox = ({ ideas, onIdeaClick, onSeeMore, limit = 5 }: TopIdeasBoxProps) => {
  const { t } = useLanguage();
  const topIdeas = ideas
    .filter(idea => !idea.is_favorited && !idea.is_used)
    .sort((a, b) => (b.viral_score || 0) - (a.viral_score || 0))
    .slice(0, limit);

  const getViralScoreColor = (score: number) => {
    const normalizedScore = Math.max(0, Math.min(100, score));
    const hue = (normalizedScore / 100) * 120;
    return `hsl(${hue}, 70%, 45%)`;
  };

  if (topIdeas.length === 0) return null;

  return (
    <div className="w-full">
      <div className="glass-card rounded-2xl overflow-hidden border border-border/50">
        <CardHeader className="pb-4 border-b border-border/30">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center shadow-sm">
                <Flame className="h-6 w-6 text-orange-500" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-foreground">
                  Top {limit} pomysły na treści
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Twoje najlepsze pomysły z ostatnich 14 dni
                </p>
              </div>
            </div>
            {/* Time period badge */}
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted/60 border border-border/40 text-[10px] md:text-xs text-muted-foreground font-medium flex-shrink-0">
              <Calendar className="w-3 h-3" />
              <span>ostatnie 14 dni</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-3">
            {topIdeas.map((idea, index) => {
              const viralScore = idea.viral_score || 0;
              const borderColor = getViralScoreColor(viralScore);
              
              return (
                <div
                  key={idea.id}
                  onClick={() => onIdeaClick(idea)}
                  className="group relative flex items-start gap-3 md:gap-4 p-3 md:p-4 rounded-xl bg-background/50 hover:bg-background/80 border border-border/40 hover:border-primary/30 cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
                >
                  {/* Rank Badge with Viral Score below on mobile */}
                  <div className="flex flex-col items-center gap-2 flex-shrink-0">
                    <div 
                      className="w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold text-sm md:text-base shadow-sm"
                      style={{
                        background: `linear-gradient(135deg, ${borderColor}15, ${borderColor}25)`,
                        color: borderColor,
                        border: `2px solid ${borderColor}40`
                      }}
                    >
                      {index + 1}
                    </div>
                    
                    {/* Viral Score - below rank on mobile, moved to right on desktop */}
                    <div className="md:hidden flex items-center gap-1 px-2 py-1 rounded-lg bg-background border border-border/50 shadow-sm">
                      <TrendingUp className="h-3 w-3" style={{ color: borderColor }} />
                      <span className="font-bold text-xs" style={{ color: borderColor }}>
                        {viralScore.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground text-sm md:text-base mb-1 group-hover:text-primary transition-colors">
                      {idea.title}
                    </h3>
                    {idea.description && (
                      <p className="text-xs text-muted-foreground">
                        {idea.description}
                      </p>
                    )}
                  </div>
                  
                  {/* Viral Score - desktop only */}
                  <div className="hidden md:flex items-center gap-2 flex-shrink-0">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background border border-border/50 shadow-sm">
                      <TrendingUp className="h-4 w-4" style={{ color: borderColor }} />
                      <span className="font-bold text-sm" style={{ color: borderColor }}>
                        {viralScore.toFixed(2)}
                      </span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </div>
    </div>
  );
};

export default TopIdeasBox;